import { useCallback } from 'react'
import { useCreateCropSchedulesBulk, useCreateCropPhasesBulk, useUpdateSeason } from '~/api/supabase-mutations'
import { supabaseGetter } from '~/api/supabase-getter'
import { buildSeasonEndOperations, buildCarryoverSchedulePhases } from '../generators/end-season-generator'

export const useEndSeason = () => {
  const { createSchedulesBulk } = useCreateCropSchedulesBulk()
  const { createPhasesBulk } = useCreateCropPhasesBulk()
  const { updateSeason } = useUpdateSeason()

  /**
   * @param {Object} params
   * @param {string} params.currentSeasonId
   * @param {Array}  params.cropsToKeep
   * @param {string} params.nextSeasonId
   * @param {string} params.userId
   * @param {{ lfd: Date, ffd: Date }|null} params.frostDates - Next year frost dates for phase generation
   */
  const endSeason = useCallback(async ({ currentSeasonId, cropsToKeep, nextSeasonId, userId, frostDates }) => {
    // 1. Create schedule headers for carried-over crops
    const { newSchedules } = buildSeasonEndOperations({ nextSeasonId, cropsToKeep, createdByUserId: userId })

    if (newSchedules.length > 0) {
      const { data: createdSchedules, error: scheduleError } = await createSchedulesBulk(newSchedules)
      if (scheduleError) return { error: scheduleError }

      // 2. Generate and insert phases when frost dates are available
      if (frostDates?.lfd && frostDates?.ffd && createdSchedules?.length) {
        const [{ data: archetypes }, { data: phaseTypes }] = await Promise.all([
          supabaseGetter.cropArchetypes.getAll(),
          supabaseGetter.cropPhaseTypes.getAll(),
        ])

        const allPhases = createdSchedules.flatMap((schedule, i) => {
          const crop = cropsToKeep[i]
          const archetype = (archetypes ?? []).find(a => a.code === crop.crop_details?.archetype_code) ?? null
          return buildCarryoverSchedulePhases({
            scheduleId: schedule.id,
            crop,
            archetype,
            frostDates,
            phaseTypes: phaseTypes ?? [],
          })
        })

        if (allPhases.length > 0) {
          const { error: phaseError } = await createPhasesBulk(allPhases)
          if (phaseError) return { error: phaseError }
        }
      }
    }

    // 3. Mark season as ended — implicitly "archives" all its schedules
    const { error: updateError } = await updateSeason(currentSeasonId, {
      state: 'ended',
      ended_at: new Date().toISOString(),
    })
    if (updateError) return { error: updateError }

    return { error: null }
  }, [createSchedulesBulk, createPhasesBulk, updateSeason])

  return { endSeason }
}
